/**
 * CassavaChain Magic Link Foundation Contract (Phase 2)
 *
 * This module is intentionally side-effect free. It does not import Firebase,
 * React, App runtime modules, storage helpers, or DOM/browser globals. The
 * production auth flow does not import or execute this module in Phase 2.
 */

export const MAGIC_LINK_PHASE = Object.freeze({
  FOUNDATION_CONTRACT: 'phase2_foundation_contract',
});

export const MAGIC_LINK_RUNTIME_VERSION = 'magic-link-runtime-phase1-completion-stability-2026-05-21';

export const MAGIC_LINK_STAGES = Object.freeze({
  IDLE: 'idle',
  SEND_REQUESTED: 'send_requested',
  LINK_SENT: 'link_sent',
  LINK_OPENED: 'link_opened',
  VERIFYING: 'verifying',
  VERIFIED: 'verified',
  FAILED: 'failed',
  EXPIRED: 'expired',
  USED: 'used',
  MISSING_EMAIL: 'missing_email',
  HANDOFF_LOST: 'handoff_lost',
  NO_CONFIRMED_RESULT: 'no_confirmed_result',
  VERIFYING_TIMEOUT: 'verifying_timeout',
  POST_SUCCESS_HYDRATION_TIMEOUT: 'post_success_hydration_timeout',
});

export const MAGIC_LINK_EVENTS = Object.freeze({
  SEND_REQUESTED: 'MAGIC_LINK_SEND_REQUESTED',
  SEND_SUCCEEDED: 'MAGIC_LINK_SEND_SUCCEEDED',
  SEND_FAILED: 'MAGIC_LINK_SEND_FAILED',
  OPENED: 'MAGIC_LINK_OPENED',
  VERIFY_STARTED: 'MAGIC_LINK_VERIFY_STARTED',
  VERIFY_SUCCEEDED: 'MAGIC_LINK_VERIFY_SUCCEEDED',
  VERIFY_FAILED: 'MAGIC_LINK_VERIFY_FAILED',
  EMAIL_MISSING: 'MAGIC_LINK_EMAIL_MISSING',
  EXPIRED: 'MAGIC_LINK_EXPIRED',
  USED_OR_INVALID: 'MAGIC_LINK_USED_OR_INVALID',
  HANDOFF_LOST: 'MAGIC_LINK_HANDOFF_LOST',
  NO_CONFIRMED_RESULT: 'MAGIC_LINK_NO_CONFIRMED_RESULT',
  USER_HANDOFF_READY: 'MAGIC_LINK_USER_HANDOFF_READY',
});

export const MAGIC_LINK_OUTCOMES = Object.freeze({
  SENT: 'sent',
  SUCCESS: 'success',
  EXPIRED: 'expired',
  USED_OR_INVALID: 'used_or_invalid',
  MISSING_EMAIL: 'missing_email',
  MISSING_EMAIL_CONTEXT: 'missing_email_context',
  HANDOFF_LOST: 'handoff_lost',
  NO_CONFIRMED_RESULT: 'no_confirmed_result',
  NETWORK_ERROR: 'network_error',
  USER_DISABLED: 'user_disabled',
  INVALID_EMAIL: 'invalid_email',
  UNAUTHORIZED: 'unauthorized',
  VERIFYING_TIMEOUT: 'verifying_timeout',
  POST_SUCCESS_HYDRATION_TIMEOUT: 'post_success_hydration_timeout',
  ERROR: 'error',
});

export const MAGIC_LINK_VISUAL_STATES = Object.freeze({
  SENDING: 'magic_link_sending',
  SENT: 'magic_link_sent',
  VERIFYING: 'magic_link_verifying',
  SUCCESS_HANDOFF: 'magic_link_success_handoff',
  EXPIRED: 'magic_link_expired',
  USED_OR_INVALID: 'magic_link_used_or_invalid',
  HANDOFF_LOST: 'magic_link_handoff_lost',
  NETWORK_ERROR: 'magic_link_network_error',
  VERIFYING_TIMEOUT: 'magic_link_verifying_timeout',
  POST_SUCCESS_HYDRATION_TIMEOUT: 'magic_link_post_success_hydration_timeout',
  ERROR: 'magic_link_error',
});

export const MAGIC_LINK_STORAGE_KEYS = Object.freeze({
  EMAIL_FOR_SIGN_IN: 'cassava.magicLink.emailForSignIn.v1',
  HANDOFF: 'cassava.magicLink.handoff.v1',
  LAST_SENT_AT: 'cassava.magicLink.lastSentAt.v1',
  VERIFY_OUTCOME: 'cassava.magicLink.verifyOutcome.v1',
});

export const MAGIC_LINK_STORAGE_TTL_MS = Object.freeze({
  EMAIL_FOR_SIGN_IN: 15 * 60 * 1000,
  HANDOFF: 10 * 60 * 1000,
  LAST_SENT_AT: 15 * 60 * 1000,
});

export const MAGIC_LINK_TTL_MS = Object.freeze({
  // Attempt markers coordinate app/entry-classifier ownership of an active link.
  ATTEMPT: 15 * 60 * 1000,
  // Email context survives cross-app/browser returns long enough for normal link use.
  EMAIL_CONTEXT: MAGIC_LINK_STORAGE_TTL_MS.EMAIL_FOR_SIGN_IN,
  // Handoff context is shorter because it is only needed during the return window.
  HANDOFF: MAGIC_LINK_STORAGE_TTL_MS.HANDOFF,
  // Replay markers are intentionally short and only suppress duplicate consumes.
  CONSUME_REPLAY_MARKER: 2 * 60 * 1000,
});

export const MAGIC_LINK_ERROR_CODES = Object.freeze({
  EXPIRED_ACTION_CODE: 'auth/expired-action-code',
  INVALID_ACTION_CODE: 'auth/invalid-action-code',
  INVALID_CONTINUE_URI: 'auth/invalid-continue-uri',
  MISSING_EMAIL: 'auth/missing-email',
  INVALID_EMAIL: 'auth/invalid-email',
  USER_DISABLED: 'auth/user-disabled',
  NETWORK_REQUEST_FAILED: 'auth/network-request-failed',
  ARGUMENT_ERROR: 'auth/argument-error',
  PERMISSION_DENIED: 'permission-denied',
  UNAUTHENTICATED: 'unauthenticated',
});

const MAGIC_SIGN_IN_MODE = 'signIn';

const toLowerSafe = (value) => (typeof value === 'string' ? value.toLowerCase() : '');
const toTrimmedString = (value) => (value == null ? '' : String(value).trim());

export function normalizeMagicLinkErrorCode(error) {
  if (!error) return '';
  return toTrimmedString(error.code || error.error || error.name);
}

export function normalizeMagicLinkErrorMessage(error) {
  if (!error) return '';
  return toTrimmedString(error.message || error.errorDescription || error.error_description);
}

export function isExpiredMagicLinkError(error) {
  return normalizeMagicLinkErrorCode(error) === MAGIC_LINK_ERROR_CODES.EXPIRED_ACTION_CODE;
}

export function isUsedOrInvalidMagicLinkError(error) {
  const code = normalizeMagicLinkErrorCode(error);
  return code === MAGIC_LINK_ERROR_CODES.INVALID_ACTION_CODE || code === MAGIC_LINK_ERROR_CODES.INVALID_CONTINUE_URI;
}

export function isMissingMagicLinkEmailError(error) {
  return normalizeMagicLinkErrorCode(error) === MAGIC_LINK_ERROR_CODES.MISSING_EMAIL;
}

export function isMagicLinkNetworkError(error) {
  const code = normalizeMagicLinkErrorCode(error);
  const message = toLowerSafe(normalizeMagicLinkErrorMessage(error));
  return code === MAGIC_LINK_ERROR_CODES.NETWORK_REQUEST_FAILED ||
    message.includes('network') ||
    message.includes('failed to fetch');
}

export function isMagicLinkUserDisabled(error) {
  return normalizeMagicLinkErrorCode(error) === MAGIC_LINK_ERROR_CODES.USER_DISABLED;
}

export function isInvalidMagicLinkEmailError(error) {
  return normalizeMagicLinkErrorCode(error) === MAGIC_LINK_ERROR_CODES.INVALID_EMAIL;
}

export function isUnauthorizedMagicLinkError(error) {
  const code = normalizeMagicLinkErrorCode(error);
  return code === MAGIC_LINK_ERROR_CODES.PERMISSION_DENIED ||
    code === MAGIC_LINK_ERROR_CODES.UNAUTHENTICATED ||
    code === 'auth/user-not-found' ||
    code === 'functions/permission-denied' ||
    code === 'functions/unauthenticated';
}

export function classifyMagicLinkError(error, context = {}) {
  const normalizedCode = normalizeMagicLinkErrorCode(error);
  const normalizedMessage = toLowerSafe(normalizeMagicLinkErrorMessage(error));

  if (context.localMissingEmailForSignIn || context.reason === 'email_for_sign_in_missing') {
    return Object.freeze({
      outcome: MAGIC_LINK_OUTCOMES.HANDOFF_LOST,
      event: MAGIC_LINK_EVENTS.HANDOFF_LOST,
      stage: MAGIC_LINK_STAGES.HANDOFF_LOST,
      reason: 'local_email_for_sign_in_missing',
      code: normalizeMagicLinkErrorCode(error),
      userFacing: true,
      shouldDescribeAsExpired: false,
    });
  }

  if (normalizedMessage.includes('token_expired')) {
    return Object.freeze({
      outcome: MAGIC_LINK_OUTCOMES.EXPIRED,
      event: MAGIC_LINK_EVENTS.EXPIRED,
      stage: MAGIC_LINK_STAGES.EXPIRED,
      reason: 'magic_link_token_expired',
      code: normalizedCode,
      userFacing: true,
    });
  }

  if (normalizedMessage.includes('token_used') || normalizedMessage.includes('token_not_found')) {
    return Object.freeze({
      outcome: MAGIC_LINK_OUTCOMES.USED_OR_INVALID,
      event: MAGIC_LINK_EVENTS.USED_OR_INVALID,
      stage: MAGIC_LINK_STAGES.USED,
      reason: 'magic_link_token_used_or_missing',
      code: normalizedCode,
      userFacing: true,
      shouldDescribeAsExpired: false,
    });
  }

  if (isExpiredMagicLinkError(error)) {
    return Object.freeze({
      outcome: MAGIC_LINK_OUTCOMES.EXPIRED,
      event: MAGIC_LINK_EVENTS.EXPIRED,
      stage: MAGIC_LINK_STAGES.EXPIRED,
      reason: 'firebase_expired_action_code',
      code: normalizeMagicLinkErrorCode(error),
      userFacing: true,
    });
  }

  if (isUsedOrInvalidMagicLinkError(error)) {
    return Object.freeze({
      outcome: MAGIC_LINK_OUTCOMES.USED_OR_INVALID,
      event: MAGIC_LINK_EVENTS.USED_OR_INVALID,
      stage: MAGIC_LINK_STAGES.USED,
      reason: 'firebase_invalid_or_used_action_code',
      code: normalizeMagicLinkErrorCode(error),
      userFacing: true,
      shouldDescribeAsExpired: false,
    });
  }

  if (isMissingMagicLinkEmailError(error)) {
    return Object.freeze({
      outcome: MAGIC_LINK_OUTCOMES.MISSING_EMAIL,
      event: MAGIC_LINK_EVENTS.EMAIL_MISSING,
      stage: MAGIC_LINK_STAGES.MISSING_EMAIL,
      reason: 'firebase_missing_email',
      code: normalizeMagicLinkErrorCode(error),
      userFacing: true,
      shouldDescribeAsExpired: false,
    });
  }

  if (isInvalidMagicLinkEmailError(error)) {
    return Object.freeze({
      outcome: MAGIC_LINK_OUTCOMES.INVALID_EMAIL,
      event: MAGIC_LINK_EVENTS.VERIFY_FAILED,
      stage: MAGIC_LINK_STAGES.FAILED,
      reason: 'firebase_invalid_email',
      code: normalizeMagicLinkErrorCode(error),
      userFacing: true,
    });
  }

  if (isMagicLinkUserDisabled(error)) {
    return Object.freeze({
      outcome: MAGIC_LINK_OUTCOMES.USER_DISABLED,
      event: MAGIC_LINK_EVENTS.VERIFY_FAILED,
      stage: MAGIC_LINK_STAGES.FAILED,
      reason: 'firebase_user_disabled',
      code: normalizeMagicLinkErrorCode(error),
      userFacing: true,
    });
  }

  if (isUnauthorizedMagicLinkError(error)) {
    return Object.freeze({
      outcome: MAGIC_LINK_OUTCOMES.UNAUTHORIZED,
      event: MAGIC_LINK_EVENTS.VERIFY_FAILED,
      stage: MAGIC_LINK_STAGES.FAILED,
      reason: 'magic_link_unauthorized',
      code: normalizeMagicLinkErrorCode(error),
      userFacing: true,
    });
  }

  if (isMagicLinkNetworkError(error)) {
    return Object.freeze({
      outcome: MAGIC_LINK_OUTCOMES.NETWORK_ERROR,
      event: MAGIC_LINK_EVENTS.VERIFY_FAILED,
      stage: MAGIC_LINK_STAGES.FAILED,
      reason: 'firebase_network_request_failed',
      code: normalizeMagicLinkErrorCode(error),
      userFacing: true,
      retryable: true,
    });
  }

  return Object.freeze({
    outcome: MAGIC_LINK_OUTCOMES.ERROR,
    event: MAGIC_LINK_EVENTS.VERIFY_FAILED,
    stage: MAGIC_LINK_STAGES.FAILED,
    reason: context.reason || 'magic_link_error',
    code: normalizeMagicLinkErrorCode(error),
    message: normalizeMagicLinkErrorMessage(error),
    userFacing: true,
  });
}

function parseQueryString(queryString) {
  const params = {};
  const raw = toTrimmedString(queryString).replace(/^\?/, '').replace(/^#/, '');
  if (!raw) return params;

  raw.split('&').forEach((pair) => {
    if (!pair) return;
    const separatorIndex = pair.indexOf('=');
    const rawKey = separatorIndex >= 0 ? pair.slice(0, separatorIndex) : pair;
    const rawValue = separatorIndex >= 0 ? pair.slice(separatorIndex + 1) : '';
    const key = safeDecodeURIComponent(rawKey);
    if (!key) return;
    params[key] = safeDecodeURIComponent(rawValue);
  });

  return params;
}

function safeDecodeURIComponent(value) {
  try {
    return decodeURIComponent(String(value).replace(/\+/g, ' '));
  } catch {
    return String(value);
  }
}

function splitHrefIntoParts(href) {
  const normalizedHref = toTrimmedString(href);
  const fallback = {
    href: normalizedHref,
    search: '',
    hash: '',
    host: '',
    origin: '',
    pathname: '',
  };

  if (!normalizedHref) return fallback;

  if (typeof URL === 'function') {
    try {
      const parsed = new URL(normalizedHref, 'https://cassavachain.local');
      return {
        href: normalizedHref,
        search: parsed.search || '',
        hash: parsed.hash || '',
        host: parsed.host || '',
        origin: parsed.origin || '',
        pathname: parsed.pathname || '',
      };
    } catch {
      // Fall through to string-only parsing below.
    }
  }

  const hashIndex = normalizedHref.indexOf('#');
  const beforeHash = hashIndex >= 0 ? normalizedHref.slice(0, hashIndex) : normalizedHref;
  const hash = hashIndex >= 0 ? normalizedHref.slice(hashIndex) : '';
  const questionIndex = beforeHash.indexOf('?');
  const search = questionIndex >= 0 ? beforeHash.slice(questionIndex) : '';
  const pathname = questionIndex >= 0 ? beforeHash.slice(0, questionIndex) : beforeHash;

  return {
    ...fallback,
    search,
    hash,
    pathname,
  };
}

export function extractMagicLinkParams(input = {}) {
  const href = typeof input === 'string' ? input : toTrimmedString(input.href);
  const hrefParts = splitHrefIntoParts(href);
  const explicitSearch = typeof input === 'object' && input !== null ? input.search : '';
  const explicitHash = typeof input === 'object' && input !== null ? input.hash : '';

  const search = toTrimmedString(explicitSearch || hrefParts.search);
  const hash = toTrimmedString(explicitHash || hrefParts.hash);
  const searchParams = parseQueryString(search);
  const hashParams = parseQueryString(hash);
  const params = {
    ...hashParams,
    ...searchParams,
  };

  return Object.freeze({
    href: hrefParts.href,
    host: hrefParts.host,
    origin: hrefParts.origin,
    pathname: hrefParts.pathname,
    search,
    hash,
    params: Object.freeze({ ...params }),
    mode: params.mode || '',
    oobCode: params.oobCode || '',
    apiKey: params.apiKey || '',
    continueUrl: params.continueUrl || '',
    lang: params.lang || '',
  });
}

export function isPotentialMagicSignInUrl(input = {}) {
  const extracted = extractMagicLinkParams(input);
  const mode = toTrimmedString(extracted.mode);
  const hasMagicMode = mode === MAGIC_SIGN_IN_MODE;
  const hasOobCode = Boolean(extracted.oobCode);
  const hasContinueUrl = Boolean(extracted.continueUrl);

  return Boolean(hasMagicMode || hasOobCode || hasContinueUrl);
}

export function classifyMagicLinkUrl(input = {}) {
  const extracted = extractMagicLinkParams(input);
  const mode = toTrimmedString(extracted.mode);
  const hasMagicMode = mode === MAGIC_SIGN_IN_MODE;
  const hasOobCode = Boolean(extracted.oobCode);
  const hasApiKey = Boolean(extracted.apiKey);
  const hasContinueUrl = Boolean(extracted.continueUrl);
  const potential = Boolean(hasMagicMode || hasOobCode || hasContinueUrl);
  const readyForFirebaseCheck = Boolean(hasMagicMode && hasOobCode && hasApiKey);

  let reason = 'not_magic_link';
  if (readyForFirebaseCheck) {
    reason = 'ready_for_firebase_email_link_check';
  } else if (potential) {
    reason = 'potential_magic_link_incomplete';
  }

  return Object.freeze({
    isPotentialMagicLink: potential,
    isPotentialMagicSignInLink: Boolean(hasMagicMode && hasOobCode),
    readyForFirebaseCheck,
    reason,
    mode,
    hasOobCode,
    hasApiKey,
    hasContinueUrl,
    lang: extracted.lang,
    continueUrl: extracted.continueUrl,
    params: extracted.params,
    shouldTreatMissingOobCodeAsExpired: false,
    shouldTreatMissingModeAsFailed: false,
  });
}

function safeRandomChunk(randomSource) {
  if (typeof randomSource === 'function') {
    const value = randomSource();
    if (value != null) return String(value);
  }

  return Math.random().toString(36).slice(2, 10);
}

export function createMagicLinkHandoffSnapshot(input = {}) {
  const createdAt = Number.isFinite(input.createdAt) ? input.createdAt : Date.now();
  const ttlMs = Number.isFinite(input.ttlMs)
    ? input.ttlMs
    : MAGIC_LINK_STORAGE_TTL_MS.HANDOFF;
  const handoffId = input.handoffId || `magic_${createdAt}_${safeRandomChunk(input.randomSource)}`;

  return Object.freeze({
    handoffId,
    email: toTrimmedString(input.email),
    createdAt,
    expiresAt: createdAt + ttlMs,
    source: input.source || 'magic_link_send',
    flow: 'magic_link',
    expectedUrlHost: toTrimmedString(input.expectedUrlHost),
    ttlMs,
  });
}

export function isMagicLinkHandoffExpired(snapshot, now = Date.now()) {
  if (!snapshot || !Number.isFinite(snapshot.expiresAt)) return true;
  return now > snapshot.expiresAt;
}

export function resolveMagicLinkVisualState(outcome) {
  const normalizedOutcome = typeof outcome === 'string' ? outcome : outcome?.outcome;

  switch (normalizedOutcome) {
    case MAGIC_LINK_OUTCOMES.SENT:
      return MAGIC_LINK_VISUAL_STATES.SENT;
    case MAGIC_LINK_OUTCOMES.SUCCESS:
      return MAGIC_LINK_VISUAL_STATES.SUCCESS_HANDOFF;
    case MAGIC_LINK_OUTCOMES.EXPIRED:
      return MAGIC_LINK_VISUAL_STATES.EXPIRED;
    case MAGIC_LINK_OUTCOMES.USED_OR_INVALID:
      return MAGIC_LINK_VISUAL_STATES.USED_OR_INVALID;
    case MAGIC_LINK_OUTCOMES.HANDOFF_LOST:
    case MAGIC_LINK_OUTCOMES.MISSING_EMAIL_CONTEXT:
    case MAGIC_LINK_OUTCOMES.MISSING_EMAIL:
      return MAGIC_LINK_VISUAL_STATES.HANDOFF_LOST;
    case MAGIC_LINK_OUTCOMES.NETWORK_ERROR:
      return MAGIC_LINK_VISUAL_STATES.NETWORK_ERROR;
    case MAGIC_LINK_OUTCOMES.VERIFYING_TIMEOUT:
      return MAGIC_LINK_VISUAL_STATES.VERIFYING_TIMEOUT;
    case MAGIC_LINK_OUTCOMES.POST_SUCCESS_HYDRATION_TIMEOUT:
      return MAGIC_LINK_VISUAL_STATES.POST_SUCCESS_HYDRATION_TIMEOUT;
    case MAGIC_LINK_OUTCOMES.NO_CONFIRMED_RESULT:
      return MAGIC_LINK_VISUAL_STATES.VERIFYING;
    default:
      return MAGIC_LINK_VISUAL_STATES.ERROR;
  }
}

export function assertNoMagicLinkSideEffects() {
  return Object.freeze({
    importsFirebase: false,
    importsReact: false,
    readsStorage: false,
    writesStorage: false,
    readsDomGlobalsAtModuleTopLevel: false,
    callsProviderApi: false,
    changesProductionBehavior: false,
  });
}
